import { useEffect, useRef } from 'react';

export function UnderwaterParticles({ density = 20 }) {
  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);
  const particlesRef = useRef([]);
  const mouseRef = useRef({ x: 0.5, y: 0.5 });
  const timeRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Setup canvas size
    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);

      // Recreate particles on resize
      initParticles();
    };

    const initParticles = () => {
      const rect = canvas.getBoundingClientRect();
      particlesRef.current = [];

      for (let i = 0; i < Math.round(density); i++) {
        particlesRef.current.push({
          x: Math.random() * rect.width,
          y: Math.random() * rect.height,
          vx: (Math.random() - 0.5) * 0.2, // Very slow horizontal drift
          vy: (Math.random() - 0.5) * 0.15, // Very slow vertical drift
          size: 0.5 + Math.random() * 1.5, // 0.5-2px
          opacity: 0.1 + Math.random() * 0.15, // Very low opacity
          phase: Math.random() * Math.PI * 2,
          speed: 0.5 + Math.random() * 0.5,
        });
      }
    };

    resize();
    window.addEventListener('resize', resize);

    // Mouse interaction
    const handleMouseMove = (e) => {
      if (prefersReducedMotion) return;
      const rect = canvas.getBoundingClientRect();
      mouseRef.current.x = e.clientX - rect.left;
      mouseRef.current.y = e.clientY - rect.top;
    };

    canvas.addEventListener('mousemove', handleMouseMove);

    // Render loop
    const render = () => {
      const rect = canvas.getBoundingClientRect();
      const w = rect.width;
      const h = rect.height;

      // Clear canvas
      ctx.clearRect(0, 0, w, h);

      if (!prefersReducedMotion) {
        timeRef.current += 0.01;
      }

      // Update and draw particles
      particlesRef.current.forEach((particle) => {
        // Natural ambient drift
        const ambientX = Math.sin(timeRef.current * 0.3 + particle.phase) * 0.3;
        const ambientY = Math.cos(timeRef.current * 0.2 + particle.phase) * 0.2;

        // Mouse displacement effect
        const dx = mouseRef.current.x - particle.x;
        const dy = mouseRef.current.y - particle.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const maxDistance = 150; // Radius of mouse influence

        let mouseInfluenceX = 0;
        let mouseInfluenceY = 0;

        if (distance < maxDistance && !prefersReducedMotion) {
          const influence = 1 - distance / maxDistance;
          // Push particles away from cursor
          mouseInfluenceX = -(dx / distance) * influence * 2;
          mouseInfluenceY = -(dy / distance) * influence * 2;
        }

        // Update position
        particle.x += particle.vx + ambientX + mouseInfluenceX;
        particle.y += particle.vy + ambientY + mouseInfluenceY;

        // Wrap around edges
        if (particle.x < -10) particle.x = w + 10;
        if (particle.x > w + 10) particle.x = -10;
        if (particle.y < -10) particle.y = h + 10;
        if (particle.y > h + 10) particle.y = -10;

        // Subtle opacity flicker
        const flickerOpacity = particle.opacity + Math.sin(timeRef.current * 2 + particle.phase) * 0.03;

        // Draw particle as soft circle
        const gradient = ctx.createRadialGradient(
          particle.x,
          particle.y,
          0,
          particle.x,
          particle.y,
          particle.size * 2
        );

        gradient.addColorStop(0, `rgba(180, 190, 195, ${flickerOpacity})`);
        gradient.addColorStop(0.5, `rgba(180, 190, 195, ${flickerOpacity * 0.5})`);
        gradient.addColorStop(1, `rgba(180, 190, 195, 0)`);

        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size * 2, 0, Math.PI * 2);
        ctx.fill();
      });

      animationFrameRef.current = requestAnimationFrame(render);
    };

    render();

    // Cleanup
    return () => {
      window.removeEventListener('resize', resize);
      canvas.removeEventListener('mousemove', handleMouseMove);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [density]);

  return (
    <canvas
      ref={canvasRef}
      className="underwater-particles"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'auto',
        zIndex: 5,
      }}
    />
  );
}
